import { useEffect, useState } from 'react';
import { APP_URL, REGISTER_URL } from '../../constants';
import { useLanguage } from '../../i18n/LanguageContext';

function graphemes(text) {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(
            (part) => part.segment
        );
    }
    return [...text];
}

function Hero() {
    const { t } = useLanguage();
    const phrases = Array.isArray(t.heroTypePhrases)
        ? t.heroTypePhrases
        : [t.heroTypePhrase].filter(Boolean);
    const phraseKey = phrases.join('\u0001');
    const ghostPhrase = phrases.reduce(
        (longest, phrase) => (graphemes(phrase).length > graphemes(longest).length ? phrase : longest),
        phrases[0] || ''
    );

    const [phraseIndex, setPhraseIndex] = useState(0);
    const activePhrase = phrases[phraseIndex] || phrases[0] || '';
    const [typedCount, setTypedCount] = useState(0);
    const typedPhrase = graphemes(activePhrase).slice(0, typedCount).join('');

    useEffect(() => {
        setPhraseIndex(0);
    }, [phraseKey]);

    useEffect(() => {
        const total = graphemes(activePhrase).length;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
            setTypedCount(total);
            return undefined;
        }

        setTypedCount(0);
        let count = 0;
        let timer = 0;
        let stopped = false;

        const schedule = (step, delay) => {
            timer = window.setTimeout(() => {
                if (!stopped) step();
            }, delay);
        };

        const type = () => {
            count += 1;
            setTypedCount(count);
            if (count >= total) schedule(erase, 1100);
            else schedule(type, 44);
        };

        const erase = () => {
            count -= 1;
            setTypedCount(count);
            if (count <= 0) {
                schedule(() => {
                    if (phrases.length > 1) {
                        setPhraseIndex((index) => (index + 1) % phrases.length);
                    } else {
                        schedule(type, 0);
                    }
                }, 420);
            } else schedule(erase, 28);
        };

        schedule(type, 320);

        return () => {
            stopped = true;
            window.clearTimeout(timer);
        };
    }, [activePhrase, phrases.length]);

    return (
        <section className="hero" id="top">
            <div className="wrap">
                <div className="hero-copy">
                    <span className="eyebrow">{t.heroEyebrow}</span>
                    <h1 aria-label={t.heroTitleAria}>
                        <span className="hero-title__lead">{t.heroTitleLead}</span>
                        <span className="line2 hero-line2">
                            <span className="hero-line2__ghost" aria-hidden="true">
                                {t.heroTitleLine2}{' '}
                                <span className="grad">{ghostPhrase}</span>
                                <span className="type-caret type-caret--spacer" />
                            </span>
                            <span className="hero-line2__live" aria-hidden="true">
                                {t.heroTitleLine2}{' '}
                                <span className="grad hero-type">{typedPhrase}</span>
                                <span className="type-caret" />
                            </span>
                        </span>
                    </h1>
                    <p className="lede" dangerouslySetInnerHTML={{ __html: t.heroLedeHtml }} />
                    <nav className="svc-row" aria-label={t.heroSvcLabel}>
                        <a href={APP_URL}>{t.svcGiftCards}</a>
                        <a href={APP_URL}>{t.svcEsims}</a>
                        <a href={APP_URL}>{t.svcTravel}</a>
                        <a href="#red-packets">{t.navPackets}</a>
                        <a href="#rewards">{t.ddCashback}</a>
                    </nav>
                    <div className="hero-ctas">
                        <a className="btn btn-primary" href={REGISTER_URL}>
                            {t.createFreeAccount}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </a>
                        <a className="btn btn-ghost" href="#what">
                            {t.howItWorks}
                        </a>
                    </div>
                    <div className="trust-row">
                        <span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <path d="M5 12l5 5L20 7" />
                            </svg>
                            {t.trustPasskey}
                        </span>
                        <span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <path d="M5 12l5 5L20 7" />
                            </svg>
                            {t.trustFees}
                        </span>
                        <span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <path d="M5 12l5 5L20 7" />
                            </svg>
                            {t.trustReserved}
                        </span>
                    </div>
                </div>

                <div className="stage" aria-label={t.heroStageLabel}>
                    <div className="float f1">
                        <div className="dot" style={{ background: 'rgba(55,211,154,.15)', color: 'var(--mint)' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                            </svg>
                        </div>
                        <div>
                            <b className="mono pos">+4.20 cashback</b>
                            <span>Gift card · Gold tier</span>
                        </div>
                    </div>
                    <div className="float f2">
                        <div className="dot" style={{ background: 'rgba(83,169,243,.15)', color: 'var(--sky)' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="6" y="2" width="12" height="20" rx="2" />
                                <path d="M10 6h4" />
                            </svg>
                        </div>
                        <div>
                            <b>eSIM Japan · 10 GB</b>
                            <span>Active in 40 seconds</span>
                        </div>
                    </div>
                    <div className="float f3">
                        <div className="dot" style={{ background: 'rgba(243,198,107,.15)', color: 'var(--gold)' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 2" />
                            </svg>
                        </div>
                        <div>
                            <b>Streak · day 6</b>
                            <span>+50 APC Points</span>
                        </div>
                    </div>

                    <div className="phone">
                        <div className="screen">
                            <div className="notch" />
                            <div className="app-hi">
                                <div>
                                    Good evening<b>Mei Lin</b>
                                </div>
                                <div className="avatar">ML</div>
                            </div>
                            <div className="bal">
                                <small>{t.phoneBalLabel}</small>
                                <div className="amt mono">
                                    1,248.50 <span>{t.phoneBalUnit}</span>
                                </div>
                                <div className="peg">≈ 1,248.50 USDT · 1:1</div>
                            </div>
                            <div className="quick">
                                <div>
                                    <i>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="4" y="3" width="16" height="18" rx="3" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </i>
                                    Red Packet
                                </div>
                                <div>
                                    <i>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                    </i>
                                    Top up
                                </div>
                                <div>
                                    <i>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M5 12h14M13 6l6 6-6 6" />
                                        </svg>
                                    </i>
                                    Send
                                </div>
                                <div>
                                    <i>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="7" width="18" height="13" rx="2" />
                                            <path d="M8 7V5h8v2" />
                                        </svg>
                                    </i>
                                    Shop
                                </div>
                            </div>
                            <div className="feed">
                                <div className="feed-row">
                                    <div className="ic" style={{ background: 'rgba(226,70,63,.2)', color: '#ff8a80' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <rect x="5" y="3" width="14" height="18" rx="3" />
                                        </svg>
                                    </div>
                                    <div>
                                        <b>Red Packet from Alex</b>
                                        <span>Lucky split · 8 of 10 opened</span>
                                    </div>
                                    <em className="pos">+12.88</em>
                                </div>
                                <div className="feed-row">
                                    <div className="ic" style={{ background: 'rgba(83,169,243,.18)', color: 'var(--sky)' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                            <rect x="6" y="2" width="12" height="20" rx="2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <b>eSIM Japan 10 GB</b>
                                        <span>Travel data</span>
                                    </div>
                                    <em>−12.90</em>
                                </div>
                                <div className="feed-row">
                                    <div className="ic" style={{ background: 'rgba(55,211,154,.18)', color: 'var(--mint)' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                    </div>
                                    <div>
                                        <b>Top up · USDT on TON</b>
                                        <span>Instant, no fee</span>
                                    </div>
                                    <em className="pos">+200.00</em>
                                </div>
                                <div className="feed-row">
                                    <div className="ic" style={{ background: 'rgba(243,198,107,.18)', color: 'var(--gold)' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                            <path d="M3 9h18v12H3zM12 9v12M3 9l3-5h12l3 5" />
                                        </svg>
                                    </div>
                                    <div>
                                        <b>Gaming gift card</b>
                                        <span>Delivered to vouchers</span>
                                    </div>
                                    <em>−50.00</em>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="float f4">
                        <div className="dot" style={{ background: 'rgba(226,70,63,.18)', color: '#ff8a80' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="5" y="3" width="14" height="18" rx="3" />
                            </svg>
                        </div>
                        <div>
                            <b>Red Packet opened</b>
                            <span className="mono pos">+12.88 from Alex</span>
                        </div>
                    </div>
                    <div className="float f5">
                        <div className="dot" style={{ background: 'rgba(169,139,255,.18)', color: '#b9a2ff' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 16l20-6-3-3-6 2-6-5-2 1 4 6-4 1-2-2-2 1z" />
                            </svg>
                        </div>
                        <div>
                            <b>Hotel in Tokyo</b>
                            <span>2 nights · paid in Digital Dollars</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Hero;
